import { SEARCH_PANEL_OFFSET_EVENT } from '../constants/header';
import Splide from '@splidejs/splide';
import { unitlessBreakpoints } from '../helpers';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';

const selectors = {
  trigger: '[data-nav-trigger]',
  drawer: '[data-nav-drawer]',
  viewport: '[data-nav-drawer-viewport]',
  track: '[data-nav-drawer-track]',
  panel: '[data-nav-panel]',
  panelBody: '.header-nav-drawer__panel-body',
  hamburger: '[data-nav-hamburger]',
  close: '[data-nav-close]',
  forward: '[data-nav-forward]',
  back: '[data-nav-back]',
  upsells: '[data-header-nav-upsells]',
  upsellsCarousel: '[data-splide-carousel]',
};

// Fallback while the drawer is hidden and unmeasurable; must match
// --header-nav-panel-width in navigation.css
const DEFAULT_PANEL_WIDTH = 290;

export default class HeaderNavDrawer {
  constructor(sectionEl, headerEl = null) {
    this.sectionEl = sectionEl;
    this.headerEl = headerEl ?? sectionEl.querySelector('header');
    this.trigger = sectionEl.querySelector(selectors.trigger);
    this.drawer = sectionEl.querySelector(selectors.drawer);
    this.viewport = sectionEl.querySelector(selectors.viewport);
    this.track = sectionEl.querySelector(selectors.track);
    this.hamburger = sectionEl.querySelector(selectors.hamburger);
    this.closeBtn = sectionEl.querySelector(selectors.close);
    this.panels = sectionEl.querySelectorAll(selectors.panel);
    this.navItems = sectionEl.querySelectorAll(selectors.forward);

    this.isOpen = false;
    this.panelStack = ['root'];
    this.panelWidth = DEFAULT_PANEL_WIDTH;
    this.upsellsCarousels = new Map();

    if (!this.drawer) return;

    this._bindEvents();
    this._updatePanelWidth();
  }

  _bindEvents = () => {
    this.hamburger?.addEventListener('click', this.open);
    this.closeBtn?.addEventListener('click', this.close);

    this.drawer.addEventListener('click', event => {
      const forward = event.target.closest(selectors.forward);
      if (forward) {
        event.preventDefault();
        this._goForward(forward.dataset.navForward);
        return;
      }

      const back = event.target.closest(selectors.back);
      if (back) {
        event.preventDefault();
        this._goBack();
      }
    });

    document.addEventListener('click', this._handleOutsideClick);
    document.addEventListener('keydown', this._handleKeydown);
    window.addEventListener('resize', this._handleResize);
  };

  _updatePanelWidth = () => {
    const isDesktop = window.innerWidth >= unitlessBreakpoints.lg;

    if (!isDesktop) {
      this.panelWidth = window.innerWidth;
      return;
    }

    const panel = this.panels[0];
    if (!panel) return;

    const width = panel.getBoundingClientRect().width;
    if (width > 0) {
      this.panelWidth = width;
    }
  };

  // Desktop only: cap the drawer at the root panel's natural height so deeper
  // panels scroll inside themselves rather than stretching the drawer.
  // Read from the body's scrollHeight, which is the content height even once
  // the cap is applied.
  _updateDrawerHeight = () => {
    if (window.innerWidth < unitlessBreakpoints.lg) {
      this.drawer.style.removeProperty('--header-nav-drawer-height');
      return;
    }

    const rootBody = this.drawer
      .querySelector('[data-nav-panel-id="root"]')
      ?.querySelector(selectors.panelBody);
    if (!rootBody) return;

    const height = rootBody.scrollHeight;
    if (height > 0) {
      this.drawer.style.setProperty(
        '--header-nav-drawer-height',
        `${height}px`,
      );
    }
  };

  _handleResize = () => {
    this._updatePanelWidth();
    this._updateDrawerHeight();
    this._updateLayout();
  };

  _requestHeaderHeightUpdate = () => {
    document.dispatchEvent(new CustomEvent(SEARCH_PANEL_OFFSET_EVENT));
  };

  _syncUpsellsCarousels = () => {
    this.drawer.querySelectorAll(selectors.upsells).forEach(upsellsEl => {
      const parentPanel = upsellsEl.closest(selectors.panel);
      const panelId = parentPanel?.dataset.navPanelId;
      const isVisible =
        panelId &&
        this.panelStack.includes(panelId) &&
        !parentPanel.hasAttribute('hidden');

      const carouselEl = upsellsEl.querySelector(selectors.upsellsCarousel);
      if (!carouselEl || !isVisible) return;

      if (this.upsellsCarousels.has(carouselEl)) {
        this.upsellsCarousels.get(carouselEl).refresh();
        return;
      }

      const splide = new Splide(carouselEl, {
        perPage: 1,
        perMove: 1,
        gap: '0.75rem',
        pagination: false,
        arrows: true,
        padding: { right: '2.5rem' },
        drag: true,
        mediaQuery: 'min',
        breakpoints: {
          [unitlessBreakpoints.lg]: {
            padding: { right: 0 },
            gap: '2.5rem',
          },
        },
      }).mount();

      this.upsellsCarousels.set(carouselEl, splide);
    });
  };

  open = () => {
    this.isOpen = true;
    document.body.classList.add('is-megamenu-open');
    this.headerEl?.classList.remove('-translate-y-full');
    this.drawer.classList.remove('hidden');
    this.hamburger?.classList.add('hidden');
    this.hamburger?.setAttribute('aria-expanded', 'true');
    this.closeBtn?.classList.remove('hidden');
    disablePageScroll();
    this._updatePanelWidth();
    this._updateDrawerHeight();
    this._requestHeaderHeightUpdate();
    this._updateLayout();
  };

  close = () => {
    this.isOpen = false;
    document.body.classList.remove('is-megamenu-open');
    this.drawer.classList.add('hidden');
    this.hamburger?.classList.remove('hidden');
    this.hamburger?.setAttribute('aria-expanded', 'false');
    this.closeBtn?.classList.add('hidden');
    enablePageScroll();
    this._resetPanels();
  };

  // Panel ids follow the Liquid scheme: "l1-{i}", "l2-{i}-{j}", and "upsells-{i}"
  // (see header-nav-drawer.liquid)
  _upsellsPanelIdForL1 = l1PanelId => {
    if (window.innerWidth < unitlessBreakpoints.lg) return null;

    const [, l1Index] = l1PanelId.split('-');
    const upsellsId = `upsells-${l1Index}`;

    return this.drawer.querySelector(`[data-nav-panel-id="${upsellsId}"]`)
      ? upsellsId
      : null;
  };

  _appendUpsellsPanel = l1PanelId => {
    const upsellsId = this._upsellsPanelIdForL1(l1PanelId);
    if (upsellsId) {
      this.panelStack.push(upsellsId);
    }
  };

  _deepestNavPanel = () =>
    [...this.panelStack]
      .reverse()
      .find(id => id !== 'root' && !id.startsWith('upsells-'));

  _goForward = panelId => {
    // Clicking the deepest active nav item collapses it (upsells stays right of l1/l2)
    if (this._deepestNavPanel() === panelId) {
      const upsellsIndex = this.panelStack.findIndex(id =>
        id.startsWith('upsells-'),
      );
      if (upsellsIndex !== -1) {
        this.panelStack.splice(upsellsIndex, 1);
      }
      this.panelStack = this.panelStack.filter(id => id !== panelId);
      this._updateLayout();
      return;
    }

    if (panelId.startsWith('l2-')) {
      const [, l1Index] = panelId.split('-');
      const l1PanelId = `l1-${l1Index}`;
      this.panelStack = ['root', l1PanelId, panelId];
      this._appendUpsellsPanel(l1PanelId);
    } else if (panelId.startsWith('l1-')) {
      this.panelStack = ['root', panelId];
      this._appendUpsellsPanel(panelId);
    }

    this._updateLayout();
  };

  _goBack = () => {
    if (this.panelStack.length <= 1) return;
    this.panelStack.pop();
    this._updateLayout();
  };

  _resetPanels = () => {
    this.panelStack = ['root'];
    this._updateLayout();
  };

  _updateActiveItems = () => {
    const activeIds = new Set(this.panelStack.slice(1));

    this.navItems.forEach(item => {
      item.classList.toggle(
        'header-nav-drawer__item--active',
        activeIds.has(item.dataset.navForward),
      );
    });
  };

  _syncUpsellsPanel = isDesktop => {
    this.panelStack = this.panelStack.filter(id => !id.startsWith('upsells-'));

    if (!isDesktop) return;

    const l1Panel = this.panelStack.find(id => id.startsWith('l1-'));
    if (l1Panel) {
      this._appendUpsellsPanel(l1Panel);
    }
  };

  _updateLayout = () => {
    const isDesktop = window.innerWidth >= unitlessBreakpoints.lg;
    this._syncUpsellsPanel(isDesktop);

    const depth = this.panelStack.length - 1;
    const visibleCount = depth + 1;

    this.panels.forEach(panel => {
      const panelId = panel.dataset.navPanelId;
      const isActive = this.panelStack.includes(panelId);
      panel.toggleAttribute('hidden', !isActive);
    });

    this._updateActiveItems();

    if (isDesktop) {
      this.viewport.style.width = `${visibleCount * this.panelWidth}px`;
      this.track.style.transform = 'translateX(0)';
    } else {
      this.viewport.style.width = `${this.panelWidth}px`;
      this.track.style.transform = `translateX(-${depth * this.panelWidth}px)`;
    }

    this._syncUpsellsCarousels();
  };

  _handleOutsideClick = event => {
    if (!this.isOpen) return;

    const clickedInside =
      this.drawer.contains(event.target) ||
      this.trigger?.contains(event.target);

    if (!clickedInside) {
      this.close();
    }
  };

  _handleKeydown = event => {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      this.close();
    }
  };
}
